import PropTypes from 'prop-types';
import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import ReactModal from 'react-modal';
import VM from 'scratch-vm';
import {injectIntl, intlShape} from 'react-intl';

import ErrorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {
    getIsError,
    getIsShowingProject
} from '../reducers/project-state';
import {
    activateTab,
    BLOCKS_TAB_INDEX,
    COSTUMES_TAB_INDEX,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

import {
    closeCostumeLibrary,
    closeBackdropLibrary,
    closeTelemetryModal,
    openExtensionLibrary
} from '../reducers/modals';

import FontLoaderHOC from '../lib/font-loader-hoc.jsx';
import LocalizationHOC from '../lib/localization-hoc.jsx';
import SBFileUploaderHOC from '../lib/sb-file-uploader-hoc.jsx';
import ProjectFetcherHOC from '../lib/project-fetcher-hoc.jsx';
import TitledHOC from '../lib/titled-hoc.jsx';
import ProjectSaverHOC from '../lib/project-saver-hoc.jsx';
import storage from '../lib/storage';
import vmListenerHOC from '../lib/vm-listener-hoc.jsx';
import vmManagerHOC from '../lib/vm-manager-hoc.jsx';
import cloudManagerHOC from '../lib/cloud-manager-hoc.jsx';

import GUIComponent from '../components/gui/gui.jsx';
import {setIsScratchDesktop} from '../lib/isScratchDesktop.js';
import {getMe, logout as forumLogout, getUserToken, clearUserToken} from '../lib/forum/index.js'; // tw: 论坛登录
import {setForumUser, logoutForumUser} from '../reducers/forum-user.js';
import TWFullScreenResizerHOC from '../lib/tw-fullscreen-resizer-hoc.jsx';
import TWStageFullScreenHOC from '../lib/tw-stage-fullscreen-hoc.jsx'; // tw: 编辑器页原生 F11 式全屏（运行当前作品、退出不丢）
import TWThemeManagerHOC from './tw-theme-manager-hoc.jsx';
import {initBackgroundObserver} from '../lib/custom-background.js';
import collaborationManager from '../lib/collaboration/collaboration-manager.js';
import defaultProjectData from '../lib/default-project/project-data.js';
import loadRandomDefaultCostume from '../lib/random-default-costume.js';
import {countProjectBlocks} from '../lib/project-blocks.js'; // tw: 统计加载作品的积木总数
import {setProjectBlockTotal} from '../reducers/project-block-count.js'; // tw: 积木总数 redux action

const {RequestMetadata, setMetadata, unsetMetadata} = storage.scratchFetch;

const setProjectIdMetadata = projectId => {
    // If project ID is '0' or zero, it's not a real project ID. In that case, remove the project ID metadata.
    // Same if it's null undefined.
    if (projectId && projectId !== '0') {
        setMetadata(RequestMetadata.ProjectId, projectId);
    } else {
        unsetMetadata(RequestMetadata.ProjectId);
    }
};

class GUI extends React.Component {
    componentDidMount () {
        setIsScratchDesktop(this.props.isScratchDesktop);
        this.props.onStorageInit(storage);
        this.props.onVmInit(this.props.vm);
        setProjectIdMetadata(this.props.projectId);
        // 初始化自定义背景
        initBackgroundObserver();
        // 初始化协作管理器
        collaborationManager.setVM(this.props.vm);
        
        // 初始化积木分类图标设置（默认关闭，只有用户显式开启过才启用）
        const blockPaletteIcons = localStorage.getItem('tw-blockPaletteIcons');
        if (blockPaletteIcons === 'true') {
            document.body.classList.add('block-palette-icons-enabled');
        } else {
            document.body.classList.add('block-palette-icons-disabled');
        }
        
        // 加载随机默认造型（立即开始，和作品加载并行进行）
        loadRandomDefaultCostume(this.props.vm);
        
        // Electron 桌面端：监听主进程消息
        if (window.electronAPI) {
            this.setupElectronListeners();
        }
        // tw: 论坛自动登录——仅当用户之前登录过（本地存有个人 token）才自动恢复登录态；
        // 否则保持未登录，显示登录/注册按钮，让用户主动登录自己的账号。
        const savedToken = getUserToken();
        if (savedToken) {
            getMe()
                .then(user => {
                    if (user) this.props.onSetForumUser(user);
                })
                .catch(() => {
                    clearUserToken();
                    this.props.onLogoutForumUser();
                });
        }

        // tw: 包装 vm.loadProject，加载作品时把积木总数写入 redux，供 Loader 显示进度。
        // 放在此处可一次性覆盖所有加载入口（菜单打开/Electron/默认项目/协作等）。
        this.wrapLoadProjectForBlockCount();
    }

    // tw: 包装 vm.loadProject，统计并上报积木总数
    wrapLoadProjectForBlockCount () {
        const vm = this.props.vm;
        if (!vm || vm.__blockCountWrapped) return;
        vm.__blockCountWrapped = true;
        const originalLoadProject = vm.loadProject.bind(vm);
        const dispatchTotal = this.props.onSetProjectBlockTotal;
        vm.loadProject = (data, ...rest) => {
            // 加载开始先清零，避免残留上一个作品的总数
            dispatchTotal(0);
            // 异步统计积木总数（解析 SB3/SB2），统计完再上报
            Promise.resolve(countProjectBlocks(data))
                .then(total => {
                    if (typeof total === 'number') dispatchTotal(total);
                })
                .catch(() => {});
            return originalLoadProject(data, ...rest);
        };
    }
    
    // 设置 Electron 监听器
    setupElectronListeners() {
        const vm = this.props.vm;
        
        // 新建项目
        window.electronAPI.onProjectNew(() => {
            console.log('[Electron] 新建项目');
            // 加载默认项目
            if (defaultProjectData) {
                // 并行加载：项目加载和随机造型加载同时进行
                loadRandomDefaultCostume(vm);
                vm.loadProject(defaultProjectData).catch(err => {
                    console.error('[Electron] 新建项目失败:', err);
                });
            }
        });
        
        // 加载项目
        window.electronAPI.onProjectLoad((event, data) => {
            console.log('[Electron] 加载项目:', data.path);
            const arrayBuffer = data.data;
            vm.loadProject(arrayBuffer).catch(err => {
                console.error('[Electron] 加载项目失败:', err);
                alert('加载项目失败: ' + err.message);
            });
        });
        
        // 保存项目请求
        window.electronAPI.onProjectSaveRequest(() => {
            console.log('[Electron] 保存项目请求');
            vm.saveProjectSb3().then(sb3 => {
                // sb3 是 ArrayBuffer，转换为 Uint8Array 发送
                window.electronAPI.sendProjectSaveData(new Uint8Array(sb3));
            }).catch(err => {
                console.error('[Electron] 保存项目失败:', err);
                alert('保存项目失败: ' + err.message);
            });
        });
        
        // 加载扩展
        window.electronAPI.onExtensionLoad((event, dataUrl) => {
            console.log('[Electron] 加载扩展:', dataUrl.substring(0, 50) + '...');
            if (vm.extensionManager && vm.extensionManager.loadExtensionURL) {
                vm.extensionManager.loadExtensionURL(dataUrl).catch(err => {
                    console.error('[Electron] 加载扩展失败:', err);
                    alert('加载扩展失败: ' + err.message);
                });
            }
        });
    }
    componentDidUpdate (prevProps) {
        if (this.props.projectId !== prevProps.projectId) {
            if (this.props.projectId !== null) {
                this.props.onUpdateProjectId(this.props.projectId);
            }
            setProjectIdMetadata(this.props.projectId);
        }
        if (this.props.isShowingProject && !prevProps.isShowingProject) {
            // this only notifies container when a project changes from not yet loaded to loaded
            // At this time the project view in www doesn't need to know when a project is unloaded
            this.props.onProjectLoaded();
        }
        // tw: 协作——进入/离开「造型」「声音」标签页时通知协作管理器，
        // 进入期间暂停远端整包应用，避免协作者发来的快照把正在编辑的画布/音频编辑器冲掉。
        if (this.props.activeTabIndex !== prevProps.activeTabIndex) {
            const isAsset = this.props.activeTabIndex === COSTUMES_TAB_INDEX ||
                this.props.activeTabIndex === SOUNDS_TAB_INDEX;
            collaborationManager.setAssetEditingTab(isAsset);
        }
    }
    render () {
        if (this.props.isError) {
            throw this.props.error;
        }
        const {
            /* eslint-disable no-unused-vars */
            assetHost,
            cloudHost,
            error,
            isError,
            isScratchDesktop,
            isShowingProject,
            onProjectLoaded,
            onStorageInit,
            onUpdateProjectId,
            onVmInit,
            projectHost,
            projectId,
            /* eslint-enable no-unused-vars */
            children,
            fetchingProject,
            isLoading,
            loadingStateVisible,
            ...componentProps
        } = this.props;
        return (
            <GUIComponent
                loading={fetchingProject || isLoading || loadingStateVisible}
                {...componentProps}
            >
                {children}
            </GUIComponent>
        );
    }
}

GUI.propTypes = {
    assetHost: PropTypes.string,
    children: PropTypes.node,
    cloudHost: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchingProject: PropTypes.bool,
    intl: intlShape,
    isError: PropTypes.bool,
    isEmbedded: PropTypes.bool,
    isFullScreen: PropTypes.bool,
    isLoading: PropTypes.bool,
    isScratchDesktop: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    loadingStateVisible: PropTypes.bool,
    onProjectLoaded: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onStorageInit: PropTypes.func,
    onUpdateProjectId: PropTypes.func,
    onVmInit: PropTypes.func,
    projectHost: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    telemetryModalVisible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};

GUI.defaultProps = {
    isScratchDesktop: false,
    isTotallyNormal: false,
    onStorageInit: storageInstance => storageInstance.addOfficialScratchWebStores(),
    onProjectLoaded: () => {},
    onUpdateProjectId: () => {},
    onVmInit: (/* vm */) => {}
};

const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        activeTabIndex: state.scratchGui.editorTab.activeTabIndex,
        alertsVisible: state.scratchGui.alerts.visible,
        backdropLibraryVisible: state.scratchGui.modals.backdropLibrary,
        blocksTabVisible: state.scratchGui.editorTab.activeTabIndex === BLOCKS_TAB_INDEX,
        cardsVisible: state.scratchGui.cards.visible,
        connectionModalVisible: state.scratchGui.modals.connectionModal,
        costumeLibraryVisible: state.scratchGui.modals.costumeLibrary,
        costumesTabVisible: state.scratchGui.editorTab.activeTabIndex === COSTUMES_TAB_INDEX,
        error: state.scratchGui.projectState.error,
        isError: getIsError(loadingState),
        isEmbedded: state.scratchGui.mode.isEmbedded,
        // tw: 不再把 isEmbedded 并入 isFullScreen，否则 embed 页 isFullScreen 恒为 true，
        // 全屏按钮永远显示“退出”且永远无法触发真正的 requestStageFullscreen()（原生全屏进不去）。
        // embed 的视觉布局由 isEmbedded 单独控制，isFullScreen 只反映真实的原生全屏状态。
        isFullScreen: state.scratchGui.mode.isFullScreen,
        isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
        isRtl: state.locales.isRtl,
        isShowingProject: getIsShowingProject(loadingState),
        loadingStateVisible: state.scratchGui.modals.loadingProject,
        projectId: state.scratchGui.projectState.projectId,
        soundsTabVisible: state.scratchGui.editorTab.activeTabIndex === SOUNDS_TAB_INDEX,
        targetIsStage: (
            state.scratchGui.targets.stage &&
            state.scratchGui.targets.stage.id === state.scratchGui.targets.editingTarget
        ),
        telemetryModalVisible: state.scratchGui.modals.telemetryModal,
        tipsLibraryVisible: state.scratchGui.modals.tipsLibrary,
        usernameModalVisible: state.scratchGui.modals.usernameModal,
        settingsModalVisible: state.scratchGui.modals.settingsModal,
        customExtensionModalVisible: state.scratchGui.modals.customExtensionModal,
        fontsModalVisible: state.scratchGui.modals.fontsModal,
        unknownPlatformModalVisible: state.scratchGui.modals.unknownPlatformModal,
        invalidProjectModalVisible: state.scratchGui.modals.invalidProjectModal,
        vm: state.scratchGui.vm,
        forumUser: state.scratchGui.forumUser
    };
};

const mapDispatchToProps = dispatch => ({
    onExtensionButtonClick: () => dispatch(openExtensionLibrary()),
    onActivateTab: tab => dispatch(activateTab(tab)),
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    onRequestCloseBackdropLibrary: () => dispatch(closeBackdropLibrary()),
    onRequestCloseCostumeLibrary: () => dispatch(closeCostumeLibrary()),
    onRequestCloseTelemetryModal: () => dispatch(closeTelemetryModal()),
    onSetForumUser: user => dispatch(setForumUser(user)),
    onLogoutForumUser: () => {
        forumLogout();
        dispatch(logoutForumUser());
    },
    onSetProjectBlockTotal: total => dispatch(setProjectBlockTotal(total)) // tw: 加载作品的积木总数
});

const ConnectedGUI = injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(GUI));

// note that redux's 'compose' function is just being used as a general utility to make
// the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
// ability to compose reducers.
const WrappedGui = compose(
    LocalizationHOC,
    ErrorBoundaryHOC('Top Level App'),
    TWThemeManagerHOC, // componentDidUpdate() needs to run very early for icons to update immediately
    TWFullScreenResizerHOC,
    TWStageFullScreenHOC, // tw: 编辑器页原生 F11 式全屏（不跳转、运行当前作品、退出不丢）
    FontLoaderHOC,
    // QueryParserHOC, // tw: HOC is unused
    ProjectFetcherHOC,
    TitledHOC,
    ProjectSaverHOC,
    vmListenerHOC,
    vmManagerHOC,
    SBFileUploaderHOC,
    cloudManagerHOC
)(ConnectedGUI);

WrappedGui.setAppElement = ReactModal.setAppElement;
export default WrappedGui;
